import { debounce } from "lodash"

import Hook from "../utils/hook"
import { dragScroll, dragScrollActions } from "../utils/drag-scroll"
import { keyDirection } from "../utils/key-direction"

export default Hook(`Carousel`, {
  targets: [`item`],
  values: { adaptiveHeight: Boolean, activeIndex: Number },
  actions: [
    {
      name: `scrollEnded`,
      type: `scroll`,
      passive: true,
      debounce: 100,
    },
    {
      name: `keyNavigate`,
      type: `keydown`,
    },
    ...dragScrollActions(),
  ],

  ...dragScroll,

  scrollEndHandlers: null,
  adaptiveHeightIO: null,
  adaptiveHeightMO: null,
  adaptiveHeightTargets: null,
  debouncedAdaptHeight: null,
  debouncedItemsChanged: null,

  mounted() {
    this.el.scrollLeft = 0
    this.scrollToActive(`instant`)
    // handlers may already be added by refs initialized before this runs
    this.scrollEndHandlers ||= []
    this.adaptiveHeightTargets = new Set()
    // items can come and go after mount (e.g. `phx-update="stream"`), which
    // changes whether the carousel can still scroll either way
    this.debouncedItemsChanged = debounce(
      this.scrollEndedAction.bind(this),
      100
    )

    if (this.adaptiveHeightValue) {
      this.debouncedAdaptHeight = debounce(this.adaptHeight.bind(this), 500)

      this.adaptiveHeightIO = new IntersectionObserver(
        this.adaptiveHeightObservation.bind(this),
        {
          rootMargin: `100% 0% 100% 0%`,
          threshold: 0.1,
        }
      )

      this.adaptiveHeightMO = new MutationObserver(this.debouncedAdaptHeight)
    }
  },

  destroyed() {
    this.debouncedItemsChanged.cancel()

    if (this.adaptiveHeightValue) {
      this.adaptiveHeightIO.disconnect()
      this.adaptiveHeightMO.disconnect()
      this.debouncedAdaptHeight?.cancel()
      this.adaptiveHeightTargets.clear()
    }
  },

  itemTargetMounted(item) {
    this.debouncedItemsChanged()

    if (this.adaptiveHeightValue) {
      this.adaptiveHeightIO.observe(item)
      this.adaptiveHeightMO.observe(item, {
        attributes: true,
        childList: true,
        subtree: true,
      })
    }
  },

  itemTargetDestroyed(item) {
    this.debouncedItemsChanged()

    if (this.adaptiveHeightValue) {
      // the MutationObserver can keep the removed item: it won’t mutate anymore
      this.adaptiveHeightIO.unobserve(item)
      this.adaptiveHeightTargets.delete(item)
      this.debouncedAdaptHeight()
    }
  },

  activeIndexValueChanged() {
    this.scrollToActive()
  },

  // brings the active item as close to the center as snapping allows: items
  // snap to the start, so the carousel can only rest where one of them starts
  scrollToActive(behavior) {
    const active = this.itemTargets[this.activeIndexValue]
    if (this.activeIndexValue == null || !active) return

    const { scrollLeft, scrollWidth, clientWidth } = this.el
    const maxScrollLeft = scrollWidth - clientWidth
    // items snap with their border box start to the inner edge of the carousel
    const snapStart = this.el.getBoundingClientRect().left + this.el.clientLeft
    const startOf = (item) =>
      scrollLeft + item.getBoundingClientRect().left - snapStart
    const centered =
      startOf(active) +
      active.getBoundingClientRect().width / 2 -
      clientWidth / 2

    const left = this.itemTargets
      .map((item) => Math.min(Math.max(startOf(item), 0), maxScrollLeft))
      .reduce((nearest, start) =>
        Math.abs(start - centered) < Math.abs(nearest - centered)
          ? start
          : nearest
      )

    // without `behavior` smooth or not as set in CSS
    this.el.scrollTo(behavior ? { left, behavior } : { left })
  },

  scrollPrev() {
    this.scrollTo(`prev`)
  },

  scrollNext() {
    this.scrollTo(`next`)
  },

  addScrollEndHandler(fn) {
    this.scrollEndHandlers ||= []
    this.scrollEndHandlers.push(fn)
  },

  scrollTo(direction) {
    const items =
      direction == `next` ? this.itemTargets : [...this.itemTargets].reverse()

    items.every((item) => {
      const listRect = this.el.getBoundingClientRect()
      const itemRect = item.getBoundingClientRect()
      const itemOffset = itemRect.left - listRect.left
      const itemMarginLeft = Math.abs(
        parseInt(window.getComputedStyle(item).marginLeft || 0)
      )

      if (
        (direction == `next` &&
          itemRect.width - itemMarginLeft > listRect.width - itemOffset) ||
        (direction == `prev` && Math.round(itemOffset) < 0)
      ) {
        item.scrollIntoView({ block: `nearest` })
        return false
      }

      return true
    })
  },

  adaptiveHeightObservation(entries) {
    // if (this.adaptiveHeightMO) this.adaptiveHeightMO.disconnect()
    // else this.adaptiveHeightMO = new MutationObserver(this.adaptHeight.bind(this))

    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // this.adaptiveHeightMO.observe(entry.target, {
        //   attributes: true,
        //   childList: true,
        //   subtree: true,
        // })

        this.adaptiveHeightTargets.add(entry.target)
      } else {
        this.adaptiveHeightTargets.delete(entry.target)
      }
    })

    this.debouncedAdaptHeight()
  },

  // ← / → go to the previous / next items while the focus is in the carousel
  keyNavigateAction(e) {
    this.keyNavigate(e)
  },

  // also called by `CardsCarousel` for the focus in its header
  keyNavigate(e) {
    const direction = keyDirection(e)
    if (!direction) return

    e.preventDefault()
    this.scrollTo(direction)
  },

  scrollEndedAction() {
    this.scrollEndHandlers.forEach((fn) => fn())
  },

  dragScroller() {
    return this.el
  },

  dragItems() {
    return this.itemTargets
  },

  dragStateClass(state) {
    return `is-${state}`
  },

  adaptHeight() {
    if (!this.adaptiveHeightTargets.size) return

    const height = Math.max(
      ...[...this.adaptiveHeightTargets].map((t) => t.offsetHeight)
    )
    this.el.style.setProperty(
      `--carousel-component-height`,
      `${height / 16}rem`
    )
  },

  isBeginning() {
    return this.el.scrollLeft < 20
  },

  isEnd() {
    return (
      this.el.scrollLeft + 20 >
      this.el.scrollWidth - this.el.getBoundingClientRect().width
    )
  },
})
